import type { DefaultPaginatedFilters } from "@/api/shared/filters";
import { createService, getPaginatedFilteredPathParam } from "@/api/shared";
import { getPathParam } from "@/api/shared/crud";
import type { NoPathParams } from "@/api/shared";
import type { TAddress } from "@/api/shared/address/types";
import type { TPracticeQrUsage } from "@/api/models/qr/practiceQr/types";
import type { EReferralStatus } from "@/api/models/referral/referral";
import type {
  EConsultationStatus,
  TConsultationAppointment,
} from "@/api/models/referral/consultation";
import type { TCotreatmentDashboardPatient } from "@/api/models/treatmentPlan/cotreatmentDashboard";
import type { TJobsAvailableForUser } from "@/api/models/patient/patient";
import type { ETreatmentPlanJobState } from "@/api/models/treatmentPlan/job";

const SLUG = "treatment_plan";

export type TDashboardAssignedPractice = {
  id: string;
  name: string;
};

export type TDashboardAssignedStaff = {
  id: string;
  first_name: string;
  last_name: string;
};

export enum EReferralDirection {
  IN = "in",
  OUT = "out",
}

export interface IReferralDashboardFilters extends DefaultPaginatedFilters {
  /** Drives the Action Needed tab */
  has_actionable_jobs?: boolean;
  referral_type?: EReferralDirection;
  /** Same filter as master dashboard referrals tabs (`consultation_status` URL key). */
  consultation_status?: EConsultationStatus;
  status?: EReferralStatus;
  last_update__gte?: string;
  last_update__lte?: string;
  date_referral_sent__gte?: string;
  date_referral_sent__lte?: string;
  /** Bounds on the *current* appointment only (`current_appointment_start` column). */
  current_appointment_start__gte?: string;
  current_appointment_start__lte?: string;
  /**
   * `"<gte>,<lte>"`, both bounds required and inclusive. Matches any appointment of the
   * consultation that ever fell in the window — including replaced/cancelled ones — unlike
   * `current_appointment_start__gte/__lte`, which only looks at the current appointment.
   */
  appointment_start__range?: string;
  practice_to?: string[];
  /**
   * State of the underlying `patient_treatment_plan`, not of a job — the two share the
   * same vocabulary, hence the reused enum. Drives the Archived tab: `failed,completed`
   * there, `pending,in_progress` on every other tab. Comma-joined by `filtersToQueryString`.
   */
  state?: ETreatmentPlanJobState[];
  ordering?:
    | "last_name"
    | "-last_name"
    | "last_update"
    | "-last_update"
    | "date_referral_sent"
    | "-date_referral_sent"
    | "current_appointment_start"
    | "-current_appointment_start";
  paginate?: boolean;
}

export type TReferralReceiverPractice = {
  id: string;
  name: string;
  addresses: TAddress[];
};

/**
 * The endpoint adds no filters of its own beyond the paginated defaults, so this is an
 * alias rather than an extending interface.
 */
export type IReferralReceiverPracticeFilters = DefaultPaginatedFilters;

export type TReferralDashboardConsultation = {
  id: string;
  status: EConsultationStatus;
  status_changed_at: string | null;
  note: string | null;
  appointments: TConsultationAppointment[];
};

export type TReferralDashboardReferral = {
  id: string;
  status: EReferralStatus;
  status_changed_at: string | null;
  referral_type: string;
  created_at: string;
  accepted_at: string | null;
  practice_to: TDashboardAssignedPractice | null;
  practice_from: TDashboardAssignedPractice | null;
  staff_created_by: TDashboardAssignedStaff | null;
  price_for_referral: number | null;
  price_for_referral_items: unknown;
  consultation: TReferralDashboardConsultation | null;
};

export type TReferralDashboardQrUsage = TPracticeQrUsage;

export type TReferralDashboardItem = {
  id: string;
  patient: TCotreatmentDashboardPatient;
  last_update: string;
  /** Creation date of the *latest* referral, annotated by the backend. */
  date_referral_sent: string | null;
  current_appointment_start: string | null;
  referral: TReferralDashboardReferral | null;
  qr_usage: TReferralDashboardQrUsage | null;
  jobs_available_for_user: TJobsAvailableForUser[];
};

export default createService(SLUG, {
  dashboard: getPaginatedFilteredPathParam<
    TReferralDashboardItem,
    NoPathParams,
    IReferralDashboardFilters
  >(`${SLUG}/referral-dashboard/`, `${SLUG}/referral-dashboard/search/`),

  receiverPractices: getPathParam<TReferralReceiverPractice[]>(
    `${SLUG}/referral-dashboard/receiver-practices/`
  ),
});
